<template>
  <Intersect
    v-editable="blok"
    v-bind="{ ...(isSbPage(blok) ? {} : { id: `${blok.id}` }) }"
    :threshold="0.5"
    @enter="onIntersect"
  >
    <div class="grid grid-rows-[1fr_auto]">
      <div class="grid">
        <FimPicture
          v-if="mobile || desktop"
          class="picture col-start-1 row-start-1"
          provider="storyblok"
          :preload="preload"
          :loading="loading"
          :strict="hideMobile"
          :mobile="hideMobile ? undefined : mobile"
          :desktop="desktop"
          :focus-mobile="focusMobile"
          :focus-desktop="focusDesktop"
          :alt="alt"
          :layout="layout"
          :widths="widths"
          :sizes="sizes"
          :max-width="maxWidth"
          :width="width"
          :height="height"
          :ratio-mobile="mobileRatio"
          :ratio-desktop="desktopRatio"
          :object-fit="objectFitImage"
          :object-position="objectPositionImage"
          :modifiers="modifiers"
          :background-color="backgroundColor"
          :media-query-mobile="mediaQueryMobile"
          :media-query-desktop="mediaQueryDesktop"
        />
        <div
          v-if="isSbImage(blok) && showDisclaimer"
          class="z-20 col-start-1 row-start-1 self-end"
        >
          <ImageWarning v-if="disclaimerPosition" :blok="blok" />
          <ImageDisclaimer
            v-if="disclaimerPosition === 'on-image'"
            :blok="blok"
          />
        </div>
      </div>
      <div
        v-if="
          showDisclaimer && isSbImage(blok) && disclaimerPosition !== 'on-image'
        "
      >
        <ImageDisclaimer :blok="blok" />
      </div>
    </div>
  </Intersect>
</template>

<script setup lang="ts">
import { isSbPage, isSbImage, isSbAsset } from '~/utils/typeguards'
import { getFocusPoint } from '../composables/cmsImages'
import type { Sbasset, SbImage, SbPage } from '../types/storyblok'
import type { ObjectFit, ObjectPosition, PictureLayout } from '~/constants/ui'
import type { ImageModifiers } from '@nuxt/image'

export type CmsImageProps = {
  ratioMobile?: number | string
  ratioDesktop?: number | string
  isTeaser?: boolean
  loading?: 'lazy' | 'eager'
  preload?: boolean
  objectFit?: ObjectFit
  objectPosition?: ObjectPosition
  widths?: Partial<Record<keyof typeof BREAKPOINTS, number>>
  maxWidth?: number // 1400
  sizes?: string
  blok: SbImage | Sbasset | SbPage
  modifiers?: Partial<ImageModifiers>
  mediaQueryMobile?: string // '(max-width: 639px) and (orientation: portrait)'
  mediaQueryDesktop?: string // '(min-width: 640px), (orientation: landscape)',
  width?: number | string
  height?: number | string
  layout?: PictureLayout
  noDisclaimer?: boolean
}

const props = withDefaults(defineProps<CmsImageProps>(), {
  loading: 'lazy',
  ratioMobile: undefined,
  ratioDesktop: undefined,
  objectFit: undefined,
  objectPosition: undefined,
  widths: undefined,
  maxWidth: 1400,
  sizes: undefined,
  modifiers: () => ({ smart: true }),
  preload: false,
  mediaQueryMobile: undefined,
  mediaQueryDesktop: undefined,
  width: undefined,
  height: undefined,
  layout: 'intrinsic',
  noDisclaimer: false,
})

const mobile = computed(() => {
  if (isSbPage(props.blok)) {
    return props.blok?.teaser_image_mobile?.filename
  }

  if (isSbImage(props.blok)) {
    return props.blok?.mobile_image?.filename
  }

  return undefined
})

const desktop = computed(() => {
  if (isSbPage(props.blok)) {
    return props.blok?.teaser_image?.filename
  }

  if (isSbImage(props.blok)) {
    return props.blok?.desktop_image?.filename
  }

  if (isSbAsset(props.blok)) {
    return props.blok.filename
  }

  return undefined
})

const focusMobile = computed(() => {
  if (isSbPage(props.blok)) {
    return getFocusPoint(props.blok.teaser_image_mobile)
  }

  if (isSbImage(props.blok)) {
    return getFocusPoint(props.blok.mobile_image)
  }
  return undefined
})

const focusDesktop = computed(() => {
  if (isSbPage(props.blok)) {
    return getFocusPoint(props.blok.teaser_image)
  }

  if (isSbImage(props.blok)) {
    return getFocusPoint(props.blok.desktop_image)
  }

  if (isSbAsset(props.blok)) {
    return getFocusPoint(props.blok)
  }

  return undefined
})

const alt = computed(() => {
  if (isSbPage(props.blok)) {
    return (
      props.blok?.teaser_image?.alt ??
      props.blok?.teaser_image_mobile?.alt ??
      ''
    )
  }

  if (isSbImage(props.blok)) {
    return props.blok?.desktop_image?.alt ?? props.blok?.mobile_image?.alt ?? ''
  }

  if (isSbAsset(props.blok)) {
    return props.blok?.alt ?? ''
  }

  return undefined
})

const hideMobile = computed(() =>
  isSbImage(props.blok) ? (props.blok?.hide_mobile ?? false) : false,
)

const toNumber = (input: string | number) =>
  typeof input === 'string' ? parseFloat(input) : input

const layout = computed(() => {
  if (isSbImage(props.blok) && props.blok.layout) {
    return toPictureLayout(props.blok.layout, 'raw')
  }
  if (props.layout) {
    return props.layout
  }
  return 'raw'
})

const mobileRatio = computed(() => {
  if (isSbImage(props.blok) && props.blok.mobile_ratio) {
    return toNumber(props.blok.mobile_ratio)
  }
  if (props.ratioMobile) {
    return toNumber(props.ratioMobile)
  }
  return undefined
})

const desktopRatio = computed(() => {
  if (isSbImage(props.blok) && props.blok.desktop_ratio) {
    return toNumber(props.blok.desktop_ratio)
  }
  if (props.ratioDesktop) {
    return toNumber(props.ratioDesktop)
  }
  return undefined
})

const { trackPromotionView } = await useTrackingEvents()

const onIntersect = (_: IntersectionObserverEntry, stop: () => void) => {
  if (isStoryblokTrackingContent(props.blok)) {
    trackPromotionView(props.blok)
  }
  stop()
}

const backgroundColor = computed(() => {
  if (isSbImage(props.blok)) {
    return parseColor(props.blok.background_color)
  }
  return undefined
})

const objectFitImage = computed(() => {
  if (isSbImage(props.blok) && props.blok.object_fit) {
    return toObjectFit(props.blok.object_fit)
  }
  if (props.objectFit) {
    return props.objectFit
  }
  return undefined
})

const objectPositionImage = computed(() => {
  if (isSbImage(props.blok) && props.blok.object_position) {
    return toObjectPosition(props.blok.object_position)
  }
  if (props.objectPosition) {
    return props.objectPosition
  }
  return undefined
})

/* Legal Disclaimer */

const showDisclaimer = computed(
  () => isSbImage(props.blok) && !props.noDisclaimer,
)

const disclaimerPosition = computed(
  () => (isSbImage(props.blok) && props.blok.disclaimer_position) || 'on-image',
)
</script>
